import React from "react";
import { captureException } from "@/lib/sentry";

/**
 * WidgetBoundary — silent error boundary for dashboard widgets.
 *
 * Unlike the top-level `<ErrorBoundary>` (which renders a branded 500
 * page for an unrecoverable app-wide crash), this boundary is built
 * for INDIVIDUAL dashboard widgets — banners, nudges, mini-cards —
 * where the right user experience when a single widget crashes is to
 * just hide it and keep the rest of the page working.
 *
 * Why we need this:
 *   Sentry caught crashes in widgets like `TransporterApplicationNudge`
 *   (undefined `api` import during HMR), `HomeTrackerWidget`, and
 *   payment banners that took down the WHOLE dashboard via the global
 *   ErrorBoundary's 500 fallback. The dashboard should be effectively
 *   un-killable by one misbehaving widget — that's a 99%-vs-100%
 *   reliability gap that's worth closing on a multi-persona surface.
 *
 * Behavior:
 *   - On any render error inside `children`, render NOTHING (the
 *     widget silently disappears for this user / this session).
 *   - The exception is still captured to Sentry (with a `widget`
 *     tag = the prop `name`) so we have full visibility — silent
 *     to the user, loud to ops.
 *   - Stateful: once a widget crashes, we keep it hidden for the
 *     lifetime of this boundary instance (no infinite retry loops).
 *
 * Usage:
 *   <WidgetBoundary name="transporter-nudge">
 *     <TransporterApplicationNudge persona={persona} />
 *   </WidgetBoundary>
 */
export default class WidgetBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.warn(`[WidgetBoundary:${this.props.name || "anonymous"}]`, error);
    captureException(error, {
      tags: { widget: this.props.name || "anonymous", boundary: "widget" },
      componentStack: info?.componentStack,
    });
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}
